/**
 * awe-pdp-membership-component
 *
 * Manages the PDP "Pick the way you shop" membership toggle.
 *
 * State management:
 *   - Single source of truth: localStorage['awe:membership'], stored as
 *     JSON `{ value: 'member'|'non-member', ts: <Date.now()> }`.
 *   - Same key used by awe-cart-membership-component, so the cart drawer
 *     automatically picks up the preference set here when it opens.
 *   - A 'non-member' choice expires after 30 days (AWE_MEMBERSHIP_TTL_MS) so
 *     the shopper is re-offered membership; 'member' never expires.
 *
 * Cart sync:
 *   - Selecting 'non-member': silently removes the membership line from the cart
 *     (if present) via /cart/change.js so the drawer doesn't have to do it on open.
 *   - Selecting 'member': only writes localStorage — the cart component's
 *     connectedCallback handles the auto-add when the drawer opens.
 *
 * Cross-component UI sync:
 *   - Listens for 'membership:changed' dispatched by awe-cart-membership-component
 *     (bubbles: true) — syncs PDP radios if user changed preference in the cart.
 *   - Dispatches 'awe:membership:changed' on document so any other components
 *     on the page can react if needed.
 *
 * Variant price sync:
 *   - Listens for 'variant:update' on the parent .shopify-section. The event
 *     carries server-rendered HTML (event.detail.data.html) that already contains
 *     the correct prices for the new variant. We extract the updated price elements
 *     from that HTML and swap them into the live DOM — same pattern as
 *     awe-arrival-date.js and product-price.js.
 */

// ────────────────────────────────────────────────────────────────────────────
// Shared localStorage preference (also read/written by awe-cart-membership.js
// and awe-buy-button-price.js under the same "awe:membership" key)
// ────────────────────────────────────────────────────────────────────────────

const AWE_MEMBERSHIP_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Reads the stored membership preference, expiring a "non-member" choice
 * after AWE_MEMBERSHIP_TTL_MS so the shopper is re-offered membership.
 * Migrates legacy bare-string values (from before expiration existed) to the
 * timestamped format, starting their 30-day clock from now.
 * @returns {'member'|'non-member'|null} null when nothing valid is stored
 */
function readMembershipPreference() {
  const raw = localStorage.getItem("awe:membership");
  if (!raw) return null;

  let value;
  let ts;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && (parsed.value === "member" || parsed.value === "non-member") && typeof parsed.ts === "number") {
      ({ value, ts } = parsed);
    }
  } catch {
    // legacy bare-string value, handled below
  }

  if (value === undefined) {
    // Legacy bare-string value (or corrupt data) — migrate it, starting the clock now.
    if (raw !== "member" && raw !== "non-member") return null;
    value = raw;
    ts = Date.now();
    try {
      localStorage.setItem("awe:membership", JSON.stringify({ value, ts }));
    } catch {
      /* private browsing */
    }
  }

  if (value === "non-member" && Date.now() - ts > AWE_MEMBERSHIP_TTL_MS) {
    try {
      localStorage.removeItem("awe:membership");
    } catch {
      /* private browsing */
    }
    return null;
  }

  return value;
}

class AwePdpMembershipComponent extends HTMLElement {
  /** @type {AbortController|null} */
  #controller = null;

  /** Prevents concurrent silent-remove calls (e.g. rapid radio clicks). */
  #removeInProgress = false;

  connectedCallback() {
    this.#controller = new AbortController();

    // Restore the customer's stored preference on every connect.
    const pref = this.#loadPreference();
    this.#syncRadios(pref);
    this.#syncColumnHighlights(pref);

    this.#bindEvents();
    this.#bindVariantUpdate();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    // Do not reset #removeInProgress here: an in-flight fetch on the old
    // instance will still complete correctly even after disconnect.
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Event binding
  // ──────────────────────────────────────────────────────────────────────────

  #bindEvents() {
    const { signal } = this.#controller;

    // Radio-style checkboxes (same mutual-exclusion pattern as cart component).
    this.querySelectorAll('input[name="awePdpMembership"]').forEach(checkbox => {
      checkbox.addEventListener(
        "change",
        e => {
          const input = /** @type {HTMLInputElement} */ (e.target);
          if (!input.checked) {
            input.checked = true;
            return;
          }
          this.querySelectorAll('input[name="awePdpMembership"]').forEach(other => {
            if (other !== input) /** @type {HTMLInputElement} */ (other).checked = false;
          });
          this.#handleRadioChange(input.value);
        },
        { signal }
      );
    });

    // Sync PDP radios when the user changes preference inside the cart drawer.
    // awe-cart-membership-component dispatches 'membership:changed' with bubbles:true
    // so it reaches document. Only sync UI — no cart API calls from here.
    document.addEventListener(
      "membership:changed",
      e => {
        const value = /** @type {CustomEvent} */ (e).detail?.value;
        if (!value) return;
        const pref = value === "non-member" ? "non-member" : "member";
        this.#syncRadios(pref);
        this.#syncColumnHighlights(pref);
      },
      { signal }
    );

    // Compare Benefits / Collapse toggle.
    const toggleBtn = this.querySelector(".awe-pdp-toggle-benefits");
    if (toggleBtn) {
      toggleBtn.addEventListener(
        "click",
        () => {
          const card = toggleBtn.closest(".awe-pdp-membership-card");
          if (!card) return;
          const expanding = !card.classList.contains("awe-pdp-membership-card--expanded");
          card.classList.toggle("awe-pdp-membership-card--expanded", expanding);
          toggleBtn.setAttribute("aria-expanded", String(expanding));
        },
        { signal }
      );
    }

    // Info modal triggers (Learn More button).
    this.querySelectorAll("[data-awe-membership-info]").forEach(trigger => {
      trigger.addEventListener("click", () => this.#openModal(), { signal });
      trigger.addEventListener(
        "keydown",
        e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            this.#openModal();
          }
        },
        { signal }
      );
    });

    // Modal: close button + backdrop click (Escape handled natively by <dialog>).
    const modal = this.#getModal();
    if (modal) {
      modal.querySelector("[data-awe-modal-close]")?.addEventListener("click", () => modal.close(), { signal });
      modal.addEventListener(
        "click",
        e => {
          if (e.target === modal) modal.close();
        },
        { signal }
      );
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Variant price sync (same pattern as awe-arrival-date.js)
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Listens for variant:update on the parent section and swaps in the
   * server-rendered price elements from the response HTML.
   */
  #bindVariantUpdate() {
    const section = this.closest(".shopify-section, dialog");
    if (!section) return;

    section.addEventListener(
      "variant:update",
      event => {
        const html = event.detail?.data?.html;
        if (!html) return;

        const newComponent = html.querySelector("awe-pdp-membership-component");
        if (!newComponent) return;

        // Swap every price row (covers both member toggle and active-member card)
        this.querySelectorAll(".awe-pdp-opt__price-row").forEach((current, i) => {
          const updated = newComponent.querySelectorAll(".awe-pdp-opt__price-row")[i];
          if (updated) current.innerHTML = updated.innerHTML;
        });
      },
      { signal: this.#controller.signal }
    );
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Toggle logic
  // ──────────────────────────────────────────────────────────────────────────

  /** @param {string} value */
  #handleRadioChange(value) {
    const pref = /** @type {'member'|'non-member'} */ (value === "non-member" ? "non-member" : "member");

    this.#savePreference(pref);
    this.#syncColumnHighlights(pref);

    if (pref === "non-member") {
      // Remove membership from cart silently so the drawer is clean on open.
      this.#removeMembershipFromCartSilently();
    }
    // 'member': only localStorage write needed.
    // awe-cart-membership-component's connectedCallback auto-adds when drawer opens.

    document.dispatchEvent(new CustomEvent("awe:membership:changed", { bubbles: true, detail: { value: pref } }));
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Silent cart removal
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Fetches the current cart and removes the membership line if present.
   * No loading state shown — the cart drawer is closed while the PDP is active.
   */
  async #removeMembershipFromCartSilently() {
    if (this.#removeInProgress) return;
    this.#removeInProgress = true;

    try {
      const cart = await fetch("/cart.js").then(r => r.json());
      const memberLine = cart?.items?.find(
        /** @param {{ properties?: Record<string,string>, key: string }} item */
        item => item.properties?._awemembership === "true"
      );

      if (!memberLine) return; // Nothing to remove.

      await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: memberLine.key, quantity: 0 }),
      });
      // No cart:update dispatch — cart drawer is closed, no UI to refresh.
    } catch (err) {
      console.error("[awe-pdp-membership] Failed to silently remove membership:", err);
    } finally {
      this.#removeInProgress = false;
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // UI state
  // ──────────────────────────────────────────────────────────────────────────

  /** @param {'member'|'non-member'} value */
  #syncRadios(value) {
    this.querySelectorAll('input[name="awePdpMembership"]').forEach(radio => {
      /** @type {HTMLInputElement} */ (radio).checked = radio.value === value;
    });
  }

  /** @param {'member'|'non-member'} value */
  #syncColumnHighlights(value) {
    this.querySelectorAll(".awe-pdp-opt").forEach(opt => {
      opt.classList.toggle("awe-pdp-opt--active", /** @type {HTMLElement} */ (opt).dataset.aweOpt === value);
    });
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Modal
  // ──────────────────────────────────────────────────────────────────────────

  #openModal() {
    this.#getModal()?.showModal();
  }

  /** @returns {HTMLDialogElement|null} */
  #getModal() {
    const id = this.dataset.modalId ?? "awe-pdp-membership-modal";
    return /** @type {HTMLDialogElement|null} */ (document.getElementById(id));
  }

  // ──────────────────────────────────────────────────────────────────────────
  // localStorage — identical key + logic to awe-cart-membership-component
  // ──────────────────────────────────────────────────────────────────────────

  /** @param {'member'|'non-member'} value */
  #savePreference(value) {
    try {
      localStorage.setItem("awe:membership", JSON.stringify({ value, ts: Date.now() }));
    } catch {
      /* private browsing */
    }
  }

  /** @returns {'member'|'non-member'} */
  #loadPreference() {
    try {
      return readMembershipPreference() ?? "member";
    } catch {
      return "member";
    }
  }
}

if (!customElements.get("awe-pdp-membership-component")) {
  customElements.define("awe-pdp-membership-component", AwePdpMembershipComponent);
}
